/**
 * CR320: Editor Stability Debug Strip
 * 
 * Temporary debug strip showing hashes, sourceMode, dirty state,
 * and last state change reason. Used to verify flicker elimination.
 * Remove once stability is confirmed.
 */

import React from "react";
import { stableHash } from "@/lib/utils/stableCompare";

export type EditorSourceMode = "db" | "draft";
export type StateChangeReason = 
  | "INIT"
  | "FETCH_SAVED_SUCCESS"
  | "HYDRATE_DRAFT"
  | "HYDRATE_NO_DRAFT"
  | "USER_EDIT"
  | "SAVE_SUCCESS_CLEAR_DRAFT"
  | "DISCARD_RESTORE_SAVED"
  | "TYPE_SWITCH"
  | "STALE_FETCH_IGNORED";

export interface EditorStabilityDebugStripProps {
  dealTypeKey: string;
  variantKey: string;
  sourceMode: EditorSourceMode;
  dirty: boolean;
  savedHash: string;
  effectiveHash: string;
  lastStateChangeReason: StateChangeReason;
  hydratingNow: boolean;
  requestNonce: number;
  /** CR321: Registry ribbon_theme for this deal type */
  registryRibbonTheme?: string;
  /** CR321: Actual theme used for the dot in the dropdown */
  dotThemeUsed?: string;
}

export function EditorStabilityDebugStrip({
  dealTypeKey,
  variantKey,
  sourceMode,
  dirty,
  savedHash,
  effectiveHash,
  lastStateChangeReason,
  hydratingNow,
  requestNonce,
  registryRibbonTheme,
  dotThemeUsed,
}: EditorStabilityDebugStripProps) {
  return (
    <div className="text-[10px] font-mono bg-slate-900 text-slate-300 px-3 py-1.5 rounded flex flex-wrap gap-x-4 gap-y-0.5 items-center select-all">
      {/* Identity */}
      <span>
        <span className="text-slate-500">type=</span>
        <span className="text-cyan-400">{dealTypeKey}</span>
        <span className="text-slate-600">/{variantKey}</span>
      </span>

      {/* Source mode */}
      <span>
        <span className="text-slate-500">src=</span>
        <span className={sourceMode === "db" ? "text-green-400" : "text-amber-400"}>
          {sourceMode}
        </span>
      </span>

      {/* Dirty state */}
      <span>
        <span className="text-slate-500">dirty=</span>
        <span className={dirty ? "text-red-400 font-bold" : "text-green-400"}>
          {dirty ? "YES" : "no"}
        </span>
      </span>

      {/* Hashes */}
      <span>
        <span className="text-slate-500">saved=</span>
        <span className="text-purple-400">{savedHash.slice(0, 6)}</span>
      </span>
      <span>
        <span className="text-slate-500">eff=</span>
        <span className={savedHash === effectiveHash ? "text-green-400" : "text-amber-400"}>
          {effectiveHash.slice(0, 6)}
        </span>
      </span>

      {/* Last reason */}
      <span>
        <span className="text-slate-500">reason=</span>
        <span className="text-blue-400">{lastStateChangeReason}</span>
      </span>

      {/* Hydration */}
      {hydratingNow && (
        <span className="text-yellow-400 animate-pulse">⏳ HYDRATING</span>
      )}

      {/* CR321: Registry theme debug */}
      {registryRibbonTheme && (
        <span>
          <span className="text-slate-500">reg.theme=</span>
          <span className="text-orange-400">{registryRibbonTheme}</span>
        </span>
      )}
      {dotThemeUsed && (
        <span>
          <span className="text-slate-500">dot=</span>
          <span className={dotThemeUsed === registryRibbonTheme ? "text-green-400" : "text-red-400 font-bold"}>
            {dotThemeUsed}
            {dotThemeUsed !== registryRibbonTheme && " ⚠MISMATCH"}
          </span>
        </span>
      )}

      {/* Nonce */}
      <span>
        <span className="text-slate-500">n=</span>
        <span className="text-slate-400">{requestNonce}</span>
      </span>
    </div>
  );
}
